// @owners { team: patients }
import { type ErrorObject } from '@alto/core/types/v1/error_object';
import { ActionSheetContext, Toast, ToastContext } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { type OrderBundlingOrigin } from '@alto/scriptdash/alto/patient_app/scheduling/order_bundling/types/v1/order_bundling_origin';
import {
  AddToShipmentEndpoint,
  type AddToShipmentEndpointAddToShipmentRequest,
} from '@alto/scriptdash/alto/patient_app/scheduling/v1/add_to_shipment_endpoint';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { TypescriptUtils } from '~shared/TypescriptUtils';
// eslint-disable-next-line import/no-deprecated
import { fetchAddresses } from '~shared/actions/addresses';
import { clearCheckout, clearEditOrder, removeAutoRefills, saveOrderSucceeded } from '~shared/actions/checkout';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { getEditShipmentID } from '~shared/features/checkout/selectors/getCheckout';
import { getBundledOrder } from '~shared/features/checkout/selectors/getOrder';
import { getDeliveries } from '~shared/features/delivery/selectors/getDeliveries';
import { updateAddToShipmentError } from '~shared/features/essentials/actions';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { apiEndpointHandler } from '~shared/helpers/api';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { queries } from '~shared/queries/query-keys';
import { invalidateDeliveries } from '~shared/queries/query-keys/deliveries';
import { invalidateShipments } from '~shared/queries/query-keys/shipments';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Address } from '~shared/types';
import { homescreenMethods } from '../../homescreen/queries/queries';
import { useInvalidateMedDetails } from '../../med-details/queries/useMedDetails';
import { useInvalidateMedList } from '../../med-list/queries/useMedList';
import { ADD_TO_UPCOMING_DELIVERY_ERROR_MESSAGE, ADD_TO_UPCOMING_DELIVERY_SUCCESS_MESSAGE } from '../constants';
import { getAddToShipmentPatientErrorMessage } from '../helpers/getAddToShipmentPatientErrorMessage';
import { getEditOrderShipmentIdForAddToShipment } from '../helpers/getEditOrderShipmentIdForAddToShipment';
import { showAddToUpcomingDeliveryErrorToast } from '../helpers/showAddToUpcomingDeliveryErrorToast';
import { useOrderBundlingOptions } from './useOrderBundlingOptions';

type Props = {
  origin: OrderBundlingOrigin;
  address: Address | null | undefined;
  onBeforeNavigate?: () => void;
};

const endpoint = AddToShipmentEndpoint(apiEndpointHandler);

export const ADD_TO_SHIPMENT_MUTATION_KEY = ['addToShipment'];

export const useAddToShipment = ({ origin, address, onBeforeNavigate }: Props) => {
  const queryClient = useQueryClient();
  const {
    invalidatePrescriptionsQuery: invalidateMedDetailsPrescriptionsQuery,
    invalidateMedicationQuery: invalidateMedDetailsMedicationQuery,
  } = useInvalidateMedDetails();
  const { invalidateListQuery } = useInvalidateMedList();
  const {
    canCombine,
    earliestUpcomingShipment,
    cartShipment,
    isLoading: isBundlingOptionsLoading,
  } = useOrderBundlingOptions({
    origin,
    address_id: address?.id,
  });
  const editOrderShipmentID = useSelectorShared(getEditShipmentID);
  const { cartAutoRefills } = useCartData();
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { addToast } = useContext(ToastContext);
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const isEditing = useSelectorShared(getIsEditingExistingOrder);
  const bundledOrder = useSelectorShared((state) => getBundledOrder(state, origin));
  const deliveries = useSelectorShared(getDeliveries);
  const shipmentID = earliestUpcomingShipment?.id;
  const newPrescriptions = cartShipment?.prescriptions;
  const { mutate, isPending, isError, isSuccess } = useMutation({
    mutationKey: ADD_TO_SHIPMENT_MUTATION_KEY,
    mutationFn: (params: AddToShipmentEndpointAddToShipmentRequest) => endpoint.addToShipment(params),
    onMutate(params: AddToShipmentEndpointAddToShipmentRequest) {
      queryClient.setQueryData(queries.addToShipment.addToShipment(params).queryKey, () => ({ data: params }));
    },
  });
  const handleAddToUpcomingDelivery = () => {
    if (!shipmentID || !newPrescriptions?.length) {
      // Defense in depth when UI gating and bundling state are out of sync — do not POST.
      trackEvent({
        event: EVENTS.CHECKOUT__PLACE_ORDER_FAILED,
        params: {
          origin: ORIGIN_NAMES.ORDER_BUNDLING,
          facility_id: address?.dispensing_facility_id,
          shipment_id: shipmentID,
          prescriptionIds: [],
        },
      });
      showAddToUpcomingDeliveryErrorToast(addToast);
      return;
    }
    const orderPrescriptions = newPrescriptions.map((prescription) => ({
      id: prescription.id,
      patient_paid_shown: prescription.price,
      auto_refill_opt_in: cartAutoRefills[prescription.id],
    }));

    const eventParams = {
      origin: ORIGIN_NAMES.ORDER_BUNDLING,
      facility_id: address?.dispensing_facility_id,
      shipment_id: shipmentID,
      prescriptionIds: orderPrescriptions.map((prescription) => prescription.id),
    };
    trackEvent({ event: EVENTS.CHECKOUT__PLACE_ORDER_TAPPED, params: eventParams });

    const editShipmentPrescriptionIDs = deliveries
      .filter((delivery) => delivery.shipment_id === editOrderShipmentID)
      .map((delivery) => delivery.prescription_id)
      .filter(TypescriptUtils.isPresent);

    const params = {
      order_prescriptions: orderPrescriptions,
      shipment_id: shipmentID,
      order: bundledOrder,
      edit_order_shipment_id: getEditOrderShipmentIdForAddToShipment({
        isEditing,
        editOrderShipmentID,
        destinationShipmentID: shipmentID,
        orderPrescriptionIDs: orderPrescriptions.map((prescription) => prescription.id),
        editShipmentPrescriptionIDs,
      }),
    };

    const handleAddToShipmentFailure = (errors?: ErrorObject[] | null, fallbackMessage?: string) => {
      trackEvent({ event: EVENTS.CHECKOUT__PLACE_ORDER_FAILED, params: eventParams });
      showAddToUpcomingDeliveryErrorToast(addToast, getAddToShipmentPatientErrorMessage(errors, fallbackMessage));
    };

    mutate(params, {
      onSuccess: (response) => {
        if (response.errors?.length) {
          handleAddToShipmentFailure(response.errors);
          return;
        }
        closeActionSheet();
        trackEvent({ event: EVENTS.CHECKOUT__PLACE_ORDER_SUCCEEDED, params: eventParams });
        addToast(
          <Toast
            dismissible
            duration={5000}
            variant="success"
          >
            {ADD_TO_UPCOMING_DELIVERY_SUCCESS_MESSAGE}
          </Toast>,
        );
        dispatch(saveOrderSucceeded());
        void invalidateDeliveries();
        // eslint-disable-next-line import/no-deprecated
        dispatch(fetchAddresses());
        void invalidateShipments();
        // Refresh pricing for the destination shipment only. Invalidating
        // pricing.order._def also refetches the edit shipment checkout is still
        // subscribed to, which 404s after that shipment is bundled away.
        queryClient.invalidateQueries({
          queryKey: queries.pricing.order({
            shipmentID,
            order: undefined,
            bundlingOptionShipmentID: undefined,
          }).queryKey,
        });
        queryClient.invalidateQueries({ queryKey: homescreenMethods.fetchHomescreen._def });
        invalidateMedDetailsPrescriptionsQuery();
        invalidateMedDetailsMedicationQuery();
        invalidateListQuery();
        dispatch(updateAddToShipmentError({ error: null, quantityAdded: null }));
        dispatch(removeAutoRefills(newPrescriptions.map((prescription) => prescription.id)));
        queryClient.removeQueries({ queryKey: queries.cart.active._def });
        if (isEditing) {
          dispatch(clearEditOrder());
        } else {
          dispatch(clearCheckout());
        }
        if (onBeforeNavigate) {
          onBeforeNavigate();
        }
        navigate('RouteOrderSuccess', {
          shipmentIDs: [shipmentID],
          isEditingOrder: true,
          bundledPrescriptionIDs: newPrescriptions.map((prescription) => prescription.id),
        });
      },
      onError: (error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : ADD_TO_UPCOMING_DELIVERY_ERROR_MESSAGE;
        handleAddToShipmentFailure(undefined, errorMessage);
      },
    });
  };

  return {
    canCombine,
    handleAddToUpcomingDelivery,
    isBundlingOptionsLoading,
    isError,
    isPending,
    isSuccess,
  };
};
